import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
  Spinner,
  Text,
  Button,
  Code
} from '@chakra-ui/react';
import { Header } from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';

export default function Dashboard() {
  const { user } = useAuth();
  console.log('Current user:', user); // Debug log
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    totalEmails: 0,
    tier: 'free'
  });
  const [recentEmails, setRecentEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const auth = getAuth();

  useEffect(() => {
    if (user) {
      fetchDashboardData(user.uid);
    }
  }, [user]);

  const fetchDashboardData = async (userId) => {
    try {
      if (!userId) return;

      setLoading(true);
      const getDashboardStats = httpsCallable(functions, 'getDashboardStats');
      const result = await getDashboardStats();
      setDashboardData(result.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEmails = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be logged in to view recent emails',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    // Wait a bit to ensure Firebase is fully initialized
    if (!auth.currentUser) {
      toast({
        title: 'Info',
        description: 'Please wait a moment while we initialize your session...',
        status: 'info',
        duration: 3000,
      });
      return;
    }

    try {
      setLoadingEmails(true);
      console.log('Auth state:', auth.currentUser ? 'Authenticated' : 'Not authenticated');
      console.log('Current user:', user);
      console.log('Functions instance:', functions);
      
      const getRecentEmails = httpsCallable(functions, 'getRecentEmails');
      const result = await getRecentEmails();
      
      if (!result.data) {
        throw new Error('No data returned from getRecentEmails');
      }
      
      setRecentEmails(result.data.recentEmailSubjects || []);
      
      toast({
        title: 'Success',
        description: 'Recent emails loaded successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error fetching recent emails:', error);
      let errorMessage = 'Failed to load recent emails';
      
      if (error.code) {
        errorMessage += ` (${error.code})`;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoadingEmails(false);
    }
  };

  return (
    <>
      <Header />
      <Container maxW="container.xl" py={{ base: 3, md: 5 }} px={{ base: 4, md: 6 }} mt="70px">
      <Box mb={{ base: 4, md: 5 }}>
        
        {loading ? (
          <Box textAlign="center" py={{ base: 8, md: 10 }}>
            <Spinner size="xl" />
            <Text mt={4}>Loading...</Text>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {/* Welcome Message */}
            <Box 
              p={{ base: 4, md: 5 }} 
              shadow="sm" 
              borderRadius="lg" 
              bg="blue.50"
              display="flex"
              flexDirection="column"
              gap={3}
            >
              <Heading size="md" color="blue.700">Welcome to Email AI Companion! 👋</Heading>
              <Text color="blue.700">
                Start your journey by sending an email to: 
              </Text>
              <Code p={2} bg="blue.100" color="blue.700" fontSize={{ base: 'sm', md: 'md' }}>agent@emailed.app</Code>
              <Text color="blue.700">
                Your AI companion will respond and help manage your emails efficiently.
              </Text>
              <Text color="blue.600" fontSize="sm">
                Pro tip: Add this email to your contacts for easier access!
              </Text>
            </Box>

            {/* Stats Grid */}
            <Box 
              p={{ base: 4, md: 5 }} 
              shadow="sm" 
              borderRadius="lg" 
              bg="white"
              display="grid"
              gap={4}
              gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            >
              <Stat p={4} bg="gray.50" borderRadius="md">
                <StatLabel fontSize={{ base: 'md', md: 'lg' }}>Total Emails Sent</StatLabel>
                <StatNumber fontSize={{ base: '3xl', md: '4xl' }}>{dashboardData.totalEmails}</StatNumber>
              </Stat>
              
              <Stat p={4} bg="gray.50" borderRadius="md" position="relative">
                <StatLabel fontSize={{ base: 'md', md: 'lg' }}>Current Tier</StatLabel>
                <Box display="flex" flexDirection={{ base: 'column', sm: 'row' }} alignItems={{ base: 'flex-start', sm: 'center' }} gap={2}>
                  <StatNumber fontSize={{ base: '2xl', md: '4xl' }}>{dashboardData.tier}</StatNumber>
                  {dashboardData.tier.toLowerCase() === 'free' && (
                    <Button
                      size="sm"
                      colorScheme="purple"
                      onClick={() => navigate('/pricing')}
                      width={{ base: 'full', sm: 'auto' }}
                    >
                      Upgrade to Pro
                    </Button>
                  )}
                </Box>
              </Stat>
            </Box>

            {/* Recent Emails Section */}
            <Box 
              p={{ base: 4, md: 5 }} 
              shadow="sm" 
              borderRadius="lg" 
              bg="white"
            >
              <Box display="flex" alignItems="center" mb={4}>
                <Heading size="md">Recent Emails</Heading>
                <Button 
                  ml={4} 
                  size="sm" 
                  onClick={fetchRecentEmails}
                  isLoading={loadingEmails}
                >
                  Load Recent Emails
                </Button>
              </Box>
              
              {recentEmails?.length > 0 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  {recentEmails.map((email, index) => (
                    <Box 
                      key={index}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Box 
                        display="flex" 
                        flexDirection={{ base: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ base: 'flex-start', sm: 'center' }}
                        gap={{ base: 2, sm: 3 }}
                      >
                        <Text fontWeight="medium" noOfLines={2}>{email.subject}</Text>
                        <Text color="gray.600" fontSize="sm" flexShrink={0}>
                          {new Date(email.timestamp).toLocaleString()}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
    </>
  );
}
